/**
 * Graph Preview: Clean redraw / Line map.
 *
 * The parsed graph redrawn as a P&ID: symbols in their P&ID shapes at their place on
 * the sheet, pipes along their stored geometry, and every stored link drawn — a pipe
 * end attached to a symbol always visibly reaches it. Names, attributes and line
 * numbers are placed on screen without overlaps, by zoom level; hovering anything
 * lights up what it is linked to.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import * as d3 from 'd3'
import type { Edge, Node } from '@/service/graph'
import { buildModel, connSyms, foldAttrTexts, litSet, nodeName, type Model } from './model'
import { buildClean, buildLines, type CleanOptions, type LineMapOptions, type ViewLayout } from './views'
import { esc } from './glyphs'
import { drawingConnectorClasses } from '../connectorPortal'
import './cleanRedraw.css'

export type CleanRedrawView = 'clean' | 'lines'

interface Props {
  /** the editor store's nodes / edges (Maps) or plain arrays */
  nodes: Map<string, Node> | Node[]
  edges: Map<string, Edge> | Edge[]
  view: CleanRedrawView
  /** open the sheet a backend-linked off-sheet connector points to (same contract as the Cards view) */
  onDrawingConnectorClick?: (label: string, extraProperties?: Record<string, string>) => void
}

const DEFAULT_OPTIONS: CleanOptions = { signals: true, fittings: true, notes: false, attributes: true, connectionPoints: true }
const DEFAULT_LINE_OPTIONS: LineMapOptions = { colorBy: 'contents', weightBySize: true }
/** text shows once it is this tall on screen; it never moves, it only appears as you zoom in */
const MIN_TEXT_PX = 7

function describe(m: Model, i: number): string {
  const n = m.nodes[i]
  const lines = n.k === 's' ? [...m.nbr[i]].filter(c => m.nodes[c].k === 'c').length : 0
  const kind = n.k === 's' ? `${n.fr} · ${lines} line${lines === 1 ? '' : 's'}`
    : n.k === 'c' ? `${n.lt} · joins ${connSyms(m, i).length} symbol${connSyms(m, i).length === 1 ? '' : 's'}`
      : m.tagOwner[i] >= 0 ? `Text, attached to ${nodeName(m, m.tagOwner[i])}` : 'Text, attached to nothing'
  const attrs = n.k === 's' ? foldAttrTexts(m, i) : []
  const via = n.k === 's' ? [...new Set([...m.nbr[i]].filter(c => m.nodes[c].k === 'c').flatMap(c => connSyms(m, c)).filter(s => s !== i).map(s => nodeName(m, s)))] : []
  return `<b>${esc(nodeName(m, i))}</b><br>${esc(kind)}`
    + (attrs.length ? `<div class="crp-at">${attrs.map(a => `<span>${esc(a)}</span>`).join('')}</div>` : '')
    + (via.length ? `<div class="crp-via">Connects to ${esc(via.slice(0, 6).join(', '))}${via.length > 6 ? ` +${via.length - 6}` : ''}</div>` : '')
}

const CleanRedrawPreview = ({ nodes, edges, view, onDrawingConnectorClick }: Props) => {
  const model = useMemo(() => buildModel(Array.isArray(nodes) ? nodes : [...nodes.values()], Array.isArray(edges) ? edges : [...edges.values()]), [nodes, edges])
  // off-sheet connectors the backend paired with another sheet: clicking one opens it
  const portals = useMemo(() => {
    const byId = new Map((Array.isArray(nodes) ? nodes : [...nodes.values()]).map(n => [n.id, n]))
    const out = new Map<number, Node>()
    model.SYMS.forEach(i => { const n = byId.get(model.nodes[i].id); if (n && drawingConnectorClasses(n).includes('portal-linked')) out.set(i, n) })
    return out
  }, [model, nodes])
  const [opts, setOpts] = useState<CleanOptions>(DEFAULT_OPTIONS)
  const [lineOpts, setLineOpts] = useState<LineMapOptions>(DEFAULT_LINE_OPTIONS)
  const [showLegend, setShowLegend] = useState(true)
  const layout: ViewLayout = useMemo(() => (view === 'clean' ? buildClean(model, opts) : buildLines(model, lineOpts)), [model, view, opts, lineOpts])

  const svgRef = useRef<SVGSVGElement>(null)
  const vpRef = useRef<SVGGElement>(null)
  const layerRef = useRef<SVGGElement>(null)
  const statusRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const legendRef = useRef<HTMLDivElement>(null)
  const zoomRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null)
  const tRef = useRef<d3.ZoomTransform>(d3.zoomIdentity)
  const focusRef = useRef<{ sel: number | null; hover: number | null }>({ sel: null, hover: null })
  const queued = useRef(false)
  const fitted = useRef(false)
  // every text item with its height on the sheet; shown or hidden by zoom only
  const textEls = useRef<Array<{ el: SVGElement; h: number; on: boolean; counted: boolean }>>([])
  const lastK = useRef(-1)
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingHover = useRef<number | null>(null)

  // apply highlight classes for the hovered / selected item
  const applyFocus = () => {
    const svg = svgRef.current; if (!svg) return
    const { sel, hover } = focusRef.current, f = sel ?? hover
    const L = f != null ? litSet(model, f, view) : null
    svg.classList.toggle('has-focus', f != null)
    layerRef.current?.querySelectorAll<SVGElement>('[data-i]').forEach(el => {
      const i = Number(el.dataset.i)
      el.classList.toggle('lit', !!L && L.has(i)); el.classList.toggle('sel', i === sel)
    })
    const card = cardRef.current
    if (card) {
      if (f != null) { card.innerHTML = describe(model, f) + (portals.has(f) && onDrawingConnectorClick ? '<div class="crp-via">Click to open the connected drawing</div>' : ''); card.hidden = false } else card.hidden = true
    }
  }

  const showText = () => {
    const k = tRef.current.k
    if (k === lastK.current) return
    lastK.current = k
    let shown = 0
    for (const e of textEls.current) {
      const on = k * e.h >= MIN_TEXT_PX
      if (on !== e.on) { e.el.style.display = on ? '' : 'none'; e.on = on }
      if (on && e.counted) shown++
    }
    if (statusRef.current) statusRef.current.textContent = `${shown} of ${layout.textCount} text items readable at this zoom · zoom in to read more · hover anything to see its links`
  }
  const queueShow = () => {
    if (queued.current) return
    queued.current = true
    requestAnimationFrame(() => { queued.current = false; showText() })
  }

  /** a graph with nothing drawable (empty, or no geometry yet) has no finite bounds to fit or draw */
  const drawable = layout.bounds.every(Number.isFinite) && layout.bounds[2] > layout.bounds[0] && layout.bounds[3] > layout.bounds[1]
  /** fit the drawing to the view; false when there is nothing to fit */
  const fit = (animate: boolean): boolean => {
    const svg = svgRef.current, zoom = zoomRef.current; if (!svg || !zoom || !drawable) return false
    const b = layout.bounds, W = svg.clientWidth || 800, H = svg.clientHeight || 600
    const inset = legendRef.current ? legendRef.current.offsetWidth + 20 : 0 // keep the drawing clear of the legend
    const k = Math.min((W - inset) / ((b[2] - b[0]) * 1.04), (H - 30) / ((b[3] - b[1]) * 1.04))
    const t = d3.zoomIdentity.translate((W + inset) / 2 - k * (b[0] + b[2]) / 2, H / 2 - k * (b[1] + b[3]) / 2).scale(k)
    const sel = d3.select(svg)
    if (animate) sel.transition().duration(400).call(zoom.transform, t); else sel.call(zoom.transform, t)
    return true
  }

  // zoom behaviour (once)
  useEffect(() => {
    const svg = svgRef.current; if (!svg) return
    const zoom = d3.zoom<SVGSVGElement, unknown>().scaleExtent([0.005, 80])
      .extent((): [[number, number], [number, number]] => [[0, 0], [svg.clientWidth || 800, svg.clientHeight || 600]])
      .on('zoom', e => {
        tRef.current = e.transform
        vpRef.current?.setAttribute('transform', e.transform.toString())
        queueShow()
      })
    zoomRef.current = zoom
    d3.select(svg).call(zoom).on('dblclick.zoom', null)
    return () => { d3.select(svg).on('.zoom', null); if (hoverTimer.current) clearTimeout(hoverTimer.current) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // drawing changes: redraw, fit the first time, re-place labels
  useEffect(() => {
    const layer = layerRef.current
    if (layer) layer.innerHTML = drawable ? layout.svg : ''
    textEls.current = layer ? [...layer.querySelectorAll<SVGElement>('[data-h]')].map(el => ({ el, h: Number(el.dataset.h), on: true, counted: el.classList.contains('st') })) : []
    lastK.current = -1
    if (!fitted.current) fitted.current = fit(false) // an empty graph doesn't count: fit once real nodes arrive
    focusRef.current = { sel: null, hover: null }; pendingHover.current = null
    if (hoverTimer.current) { clearTimeout(hoverTimer.current); hoverTimer.current = null }
    applyFocus(); showText()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout])

  const itemAt = (e: React.PointerEvent | React.MouseEvent): number | null => {
    const el = (e.target as Element).closest('[data-i]') as SVGElement | null
    return el ? Number(el.dataset.i) : null
  }
  // hover only highlights, and only once the pointer rests (briefly) on an item, so sweeping
  // across the drawing doesn't flash it; it never re-places labels
  const setHover = (i: number | null) => {
    if (i === pendingHover.current) return
    pendingHover.current = i
    if (hoverTimer.current) clearTimeout(hoverTimer.current)
    hoverTimer.current = setTimeout(() => {
      hoverTimer.current = null
      if (focusRef.current.hover === i) return
      focusRef.current.hover = i
      if (focusRef.current.sel == null) applyFocus()
    }, i == null ? 160 : 70)
  }
  const onPointerMove = (e: React.PointerEvent) => setHover(itemAt(e))
  const onPointerLeave = () => setHover(null)
  const onClick = (e: React.MouseEvent) => {
    const i = itemAt(e)
    focusRef.current.sel = i; applyFocus()
    const portal = i != null ? portals.get(i) : undefined
    if (portal && onDrawingConnectorClick) onDrawingConnectorClick((portal.label ?? '').trim() || nodeName(model, i!), portal.extraProperties)
  }

  const toggle = (k: keyof CleanOptions) => setOpts(o => ({ ...o, [k]: !o[k] }))

  return (
    <div className="crp" data-testid="clean-redraw-preview">
      <svg ref={svgRef} className="crp-canvas" onPointerMove={onPointerMove} onPointerLeave={onPointerLeave} onClick={onClick}
        aria-label={view === 'clean' ? 'Clean redraw of the P&ID' : 'Line map of the P&ID'}>
        <g ref={vpRef}><g ref={layerRef} className="crp-layer" /></g>
      </svg>
      <div className="crp-bar">
        <button type="button" className="crp-btn" onClick={() => { fit(true) }}>Fit all</button>
        {view === 'clean' && (
          <>
            <label className="crp-chk"><input type="checkbox" checked={opts.attributes} onChange={() => toggle('attributes')} />Attributes</label>
            <label className="crp-chk"><input type="checkbox" checked={opts.connectionPoints} onChange={() => toggle('connectionPoints')} />Connection points</label>
            <label className="crp-chk"><input type="checkbox" checked={opts.signals} onChange={() => toggle('signals')} />Signal lines</label>
            <label className="crp-chk"><input type="checkbox" checked={opts.fittings} onChange={() => toggle('fittings')} />Fittings</label>
            <label className="crp-chk"><input type="checkbox" checked={opts.notes} onChange={() => toggle('notes')} />Notes</label>
          </>
        )}
        {view === 'lines' && (
          <>
            <span className="crp-seg" role="group" aria-label="Colour lines by">
              {([['contents', 'Colour: contents'], ['line', 'Colour: line number']] as const).map(([v, label]) => (
                <button key={v} type="button" aria-pressed={lineOpts.colorBy === v} onClick={() => setLineOpts(o => ({ ...o, colorBy: v }))}>{label}</button>
              ))}
            </span>
            <label className="crp-chk"><input type="checkbox" checked={lineOpts.weightBySize} onChange={() => setLineOpts(o => ({ ...o, weightBySize: !o.weightBySize }))} />Weight = pipe size</label>
            <label className="crp-chk"><input type="checkbox" checked={showLegend} onChange={() => setShowLegend(s => !s)} />Legend</label>
          </>
        )}
      </div>
      {view === 'lines' && showLegend && layout.legend && (
        <div ref={legendRef} className="crp-legend" data-testid="line-map-legend">
          <b>{layout.legend.title}</b>
          {layout.legend.rows.map(r => (
            <div key={r.key} className="crp-lrow">
              <span className={r.color ? 'crp-sw' : 'crp-sw none'} style={r.color ? { background: r.color } : undefined} />
              <span className="crp-lcode">{r.label}</span>
              <span className="crp-ldet">{r.detail}</span>
              <span className="crp-lnum">{r.count} {r.unit}{r.count === 1 ? '' : 's'}</span>
            </div>
          ))}
          {layout.legend.sizes.length > 0 && (
            <div className="crp-lsizes">
              <span>Line weight = pipe size</span>
              {layout.legend.sizes.map(s => (
                <span key={s.sizeIn} className="crp-lsz"><svg width="26" height="14" aria-hidden="true"><line x1="2" y1="7" x2="24" y2="7" style={{ strokeWidth: s.width }} /></svg>{s.sizeIn}&quot;</span>
              ))}
            </div>
          )}
        </div>
      )}
      <div ref={cardRef} className="crp-card" hidden />
      <div ref={statusRef} className="crp-status" />
    </div>
  )
}

export default CleanRedrawPreview
